<script setup lang="ts">
import { match } from 'ts-pattern';
import { type HTMLAttributes, computed } from 'vue';

import { cn } from '@/lib/utils';

import GiftIcon from '~icons/iconoir/gift';
import SparksIcon from '~icons/iconoir/sparks-solid';

const {
  orientation = 'left',
  size = 'md',
  color = '',
  ...props
} = defineProps<{
  size?: 'sm' | 'md' | 'lg' | 'xl';
  orientation?: 'left' | 'right';
  color?: string;
  class?: HTMLAttributes['class'];
}>();

const rotation = computed(() =>
  match(orientation)
    .with('left', () => '-rotate-12')
    .with('right', () => 'rotate-12')
    .exhaustive(),
);

const giftSize = computed(() =>
  match(size)
    .with('sm', () => 'h-8 w-8')
    .with('md', () => 'h-14 w-14')
    .with('lg', () => 'h-20 w-20')
    .with('xl', () => 'h-28 w-28')
    .exhaustive(),
);

const sparksClassNames = computed(() => {
  const sparksSize = match(size)
    .with('sm', () => 'h-5 w-5')
    .with('md', () => 'h-7 w-7')
    .with('lg', () => 'h-10 w-10')
    .with('xl', () => 'h-16 w-16')
    .exhaustive();

  const scaleClassNames = match(orientation)
    .with('left', () => '-scale-x-100')
    .with('right', () => 'scale-x-100')
    .exhaustive();

  const yAxisClassNames = match(size)
    .with('sm', () => '-top-1.5')
    .with('md', () => '-top-1.5')
    .with('lg', () => '-top-2.5')
    .with('xl', () => '-top-5')
    .exhaustive();

  const xAxisClassNames = match({ orientation, size })
    .with({ orientation: 'left', size: 'sm' }, () => '-left-3')
    .with({ orientation: 'left', size: 'md' }, () => '-left-4')
    .with({ orientation: 'left', size: 'lg' }, () => '-left-6')
    .with({ orientation: 'left', size: 'xl' }, () => '-left-10')
    .with({ orientation: 'right', size: 'sm' }, () => '-right-3')
    .with({ orientation: 'right', size: 'md' }, () => '-right-4')
    .with({ orientation: 'right', size: 'lg' }, () => '-right-6')
    .with({ orientation: 'right', size: 'xl' }, () => '-right-10')
    .exhaustive();

  return cn(sparksSize, scaleClassNames, yAxisClassNames, xAxisClassNames);
});
</script>

<template>
  <div :class="cn('relative inline-block', props.class)">
    <GiftIcon :class="cn('', color, giftSize, rotation)" />
    <SparksIcon :class="cn('absolute', color, sparksClassNames, rotation)" />
  </div>
</template>
